import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

interface SonioDemoFlowFilterContextType {
  /** Flow IDs that THIS user is assigned to (from `/profile.flowids`). */
  profileFlowIds: string[];
  setProfileFlowIds: (ids: string[]) => void;
  /** Flow IDs the user has currently ticked in the sidebar picker. */
  selectedFlowIds: string[];
  setSelectedFlowIds: (ids: string[]) => void;
  toggleFlowId: (id: string) => void;
}

export const SonioDemoFlowFilterContext = createContext<
  SonioDemoFlowFilterContextType | undefined
>(undefined);

export const useSonioDemoFlowFilter = () => {
  const context = useContext(SonioDemoFlowFilterContext);
  if (!context) {
    throw new Error(
      'useSonioDemoFlowFilter must be used within a SonioDemoFlowFilterProvider',
    );
  }
  return context;
};

export const SonioDemoFlowFilterProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [profileFlowIds, setProfileFlowIdsState] = useState<string[]>([]);
  const [selectedFlowIds, setSelectedFlowIdsState] = useState<string[]>([]);

  // Stable setters that only update when the list actually changes. Without
  // this, callers in effects can trigger infinite loops because each render
  // would produce a fresh setter reference -> effect re-runs -> setState ->
  // re-render -> ...
  const setProfileFlowIds = useCallback((ids: string[]) => {
    setProfileFlowIdsState(prev => {
      if (prev.length === ids.length && prev.every((v, i) => v === ids[i])) {
        return prev;
      }
      return ids;
    });
  }, []);

  const setSelectedFlowIds = useCallback((ids: string[]) => {
    setSelectedFlowIdsState(prev => {
      if (prev.length === ids.length && prev.every((v, i) => v === ids[i])) {
        return prev;
      }
      return ids;
    });
  }, []);

  const toggleFlowId = useCallback((id: string) => {
    setSelectedFlowIdsState(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id],
    );
  }, []);

  const value = useMemo<SonioDemoFlowFilterContextType>(
    () => ({
      profileFlowIds,
      setProfileFlowIds,
      selectedFlowIds,
      setSelectedFlowIds,
      toggleFlowId,
    }),
    [
      profileFlowIds,
      selectedFlowIds,
      setProfileFlowIds,
      setSelectedFlowIds,
      toggleFlowId,
    ],
  );

  return (
    <SonioDemoFlowFilterContext.Provider value={value}>
      {children}
    </SonioDemoFlowFilterContext.Provider>
  );
};
